import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import SafeIcon from '../common/SafeIcon'
import ProjectCard from '../components/Dashboard/ProjectCard'
import TemplateLibrary from '../components/Templates/TemplateLibrary'
import AnalyticsDashboard from '../components/Analytics/AnalyticsDashboard'
import AdminPanel from '../components/Admin/AdminPanel'
import { useProjects } from '../hooks/useProjects'
import { useAuth } from '../contexts/AuthContext'
import CreditDisplay from '../components/Credits/CreditDisplay'
import * as FiIcons from 'react-icons/fi'

const { FiPlus, FiSearch, FiFilter, FiBarChart3, FiLayout, FiShield } = FiIcons

const Dashboard = () => {
  const navigate = useNavigate()
  const { profile, getProjectLimit, checkPermission } = useAuth()
  const { projects, loading, createProject, updateProject, deleteProject, duplicateProject } = useProjects()
  const [searchTerm, setSearchTerm] = useState('')
  const [filterStatus, setFilterStatus] = useState('all')
  const [activeTab, setActiveTab] = useState('projects')
  const [refreshCredits, setRefreshCredits] = useState(null)

  // Refresh credits when profile changes (e.g., plan upgrade)
  useEffect(() => {
    if (refreshCredits && typeof refreshCredits === 'function' && profile) {
      refreshCredits()
    }
  }, [profile, refreshCredits])

  const tabs = [
    { id: 'projects', label: 'My Projects', icon: FiSearch },
    // TODO: Re-enable these features in future versions
    // { id: 'templates', label: 'Templates', icon: FiLayout },
    // { id: 'analytics', label: 'Analytics', icon: FiBarChart3 },
    ...(checkPermission('admin') ? [{ id: 'admin', label: 'Admin', icon: FiShield }] : [])
  ]

  const filteredProjects = projects.filter(project => {
    const matchesSearch = project.title.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesFilter = filterStatus === 'all' || project.status === filterStatus
    return matchesSearch && matchesFilter
  })

  const handleEdit = (projectId) => {
    navigate(`/editor/${projectId}`)
  }

  const handleDuplicate = async (projectId) => {
    const projectLimit = getProjectLimit()
    if (projectLimit !== -1 && projects.length >= projectLimit) {
      alert(`You've reached your project limit of ${projectLimit}. Upgrade to Pro for unlimited projects.`)
      return
    }

    const result = await duplicateProject(projectId)
    if (result.error) {
      alert('Error duplicating project: ' + result.error.message)
    }
  }

  const handlePreview = (projectId) => {
    navigate(`/present/${projectId}`)
  }

  const handlePublish = async (projectId) => {
    const project = projects.find(p => p.id === projectId)
    const newStatus = project.status === 'published' ? 'draft' : 'published'
    
    const result = await updateProject(projectId, { status: newStatus })
    if (result.error) {
      alert('Error updating project: ' + result.error.message)
    }
  }

  const handleDelete = async (projectId) => {
    if (confirm('Are you sure you want to delete this project?')) {
      const result = await deleteProject(projectId)
      if (result.error) {
        alert('Error deleting project: ' + result.error.message)
      }
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-pulse">
          <div className="text-lg text-gray-600">Loading your projects...</div>
        </div>
      </div>
    )
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            Dashboard
            {profile?.role && (
              <span className={`ml-3 text-sm px-2 py-1 rounded-full ${
                profile.role === 'admin' ? 'bg-red-100 text-red-700' :
                profile.role === 'pro' ? 'bg-blue-100 text-blue-700' :
                'bg-gray-100 text-gray-700'
              }`}>
                {profile.role.toUpperCase()}
              </span>
            )}
          </h1>
          <p className="text-gray-600 mt-1">
            Welcome back! You have {projects.length} project{projects.length !== 1 ? 's' : ''} in your workspace.
          </p>
        </div>
        <button
          onClick={() => {
            const projectLimit = getProjectLimit()
            if (projectLimit !== -1 && projects.length >= projectLimit) {
              alert(`You've reached your project limit of ${projectLimit}. Upgrade to Pro for unlimited projects.`)
              return
            }
            navigate('/create')
          }}
          className="bg-primary-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-primary-700 transition-colors flex items-center space-x-2 shadow-lg hover:shadow-xl"
        >
          <SafeIcon icon={FiPlus} className="w-5 h-5" />
          <span>New Project</span>
        </button>
      </div>

      {/* Tabs - only show if there are multiple tabs */}
      {tabs.length > 1 && (
        <div className="flex space-x-1 mb-8 bg-gray-100 p-1 rounded-lg w-fit">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-md font-medium transition-colors ${
                activeTab === tab.id
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <SafeIcon icon={tab.icon} className="w-4 h-4" />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>
      )}

      {/* Tab Content */}
      {activeTab === 'projects' && (
        <>
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Projects</p>
                  <p className="text-2xl font-bold text-gray-900">{projects.length}</p>
                </div>
                <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                  <SafeIcon icon={FiPlus} className="w-6 h-6 text-primary-600" />
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Published</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {projects.filter(p => p.status === 'published').length}
                  </p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <SafeIcon icon={FiSearch} className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Drafts</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {projects.filter(p => p.status === 'draft').length}
                  </p>
                </div>
                <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                  <SafeIcon icon={FiFilter} className="w-6 h-6 text-yellow-600" />
                </div>
              </div>
            </div>
          </div>

          {/* AI Credits Row */}
          <CreditDisplay 
            compact={false} 
            showHistory={false} 
            dashboardLayout={true} 
            onRefreshCredits={setRefreshCredits}
          />

          {/* Search & Filter */}
          <div className="flex items-center space-x-4 mb-8">
            <div className="relative flex-1 max-w-md">
              <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search projects..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent shadow-sm"
              />
            </div>
            <div className="relative">
              <SafeIcon icon={FiFilter} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="pl-10 pr-8 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent appearance-none bg-white shadow-sm"
              >
                <option value="all">All Projects</option>
                <option value="draft">Drafts</option>
                <option value="published">Published</option>
              </select>
            </div>
          </div>

          {/* Projects Grid */}
          {filteredProjects.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <SafeIcon icon={FiPlus} className="w-12 h-12 text-gray-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {searchTerm || filterStatus !== 'all' ? 'No projects found' : 'Ready to create your first project?'}
              </h3>
              <p className="text-gray-600 mb-8 max-w-md mx-auto">
                {searchTerm || filterStatus !== 'all'
                  ? 'Try adjusting your search criteria or filter settings to find what you\'re looking for.'
                  : 'Start building professional slide decks for your film and TV projects with AI-powered assistance.'
                }
              </p>
              {!searchTerm && filterStatus === 'all' && (
                <button
                  onClick={() => {
                    const projectLimit = getProjectLimit()
                    if (projectLimit !== -1 && projects.length >= projectLimit) {
                      alert(`You've reached your project limit of ${projectLimit}. Upgrade to Pro for unlimited projects.`)
                      return
                    }
                    navigate('/create')
                  }}
                  className="bg-primary-600 text-white px-8 py-4 rounded-lg font-medium hover:bg-primary-700 transition-colors shadow-lg hover:shadow-xl"
                >
                  Create Your First Project
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredProjects.map(project => (
                <ProjectCard
                  key={project.id}
                  project={project}
                  onEdit={handleEdit}
                  onDuplicate={handleDuplicate}
                  onPreview={handlePreview}
                  onPublish={handlePublish}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          )}
        </>
      )}

      {activeTab === 'templates' && <TemplateLibrary />}
      {activeTab === 'analytics' && <AnalyticsDashboard />}
      {activeTab === 'admin' && checkPermission('admin') && <AdminPanel />}
    </div>
  )
}

export default Dashboard